import {announce} from '@github-ui/aria-live'
import {useCallback} from 'react'

import {useRunActionRequiredWorkflows} from './mutations/use-run-action-required-workflows'

export interface UseApproveWorkflowsParams {
  pullRequestHeadSha: string
  /** Invoked after workflows are successfully approved and re-requested (e.g. to move focus). */
  onApproved?: () => void
  /** Invoked with the error message when approving and re-requesting workflows fails. */
  onError?: (message: string) => void
}

export interface UseApproveWorkflowsResult {
  /** Approves and re-requests the workflows that are pending approval. */
  approveWorkflows: () => void
  /** Whether the approval request is in flight. */
  isPending: boolean
}

/**
 * Encapsulates the "approve workflows to run" behaviour used when a pull request has
 * workflow runs awaiting approval. Wraps the underlying mutation and tracks the in-flight
 * state. Consumers handle failures via the `onError` callback so they can track and render
 * their own error state.
 */
export function useApproveWorkflows({
  pullRequestHeadSha,
  onApproved,
  onError,
}: UseApproveWorkflowsParams): UseApproveWorkflowsResult {
  const {isPending, mutate: runActionRequiredWorkflows} = useRunActionRequiredWorkflows({pullRequestHeadSha})

  const approveWorkflows = useCallback(() => {
    runActionRequiredWorkflows(undefined, {
      onSuccess: () => {
        setTimeout(() => announce('Successfully approved and re-requested workflows.'), 1000)
        onApproved?.()
      },
      onError: (e: Error): void => {
        announce(e.message)
        onError?.(e.message)
      },
    })
  }, [runActionRequiredWorkflows, onApproved, onError])

  return {approveWorkflows, isPending}
}
